import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PhotoInput from "../components/PhotoInput";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const lbl = "text-[10px] font-semibold uppercase tracking-wider text-slate-500";
const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

function PnLCard({ life }) {
  if (!life) return null;
  return (
    <div className="mt-2 rounded-xl bg-[#0E2240] p-3 text-white">
      <div className="text-[9px] uppercase tracking-widest text-[#BF9B30]">Lifetime P&L</div>
      <div className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-1.5 text-[11px]">
        {[["Months", life.months_in_fleet], ["Km run", (life.total_km || 0).toLocaleString("en-IN")], ["Test drives", life.total_test_drives],
          ["EMI paid", inr(life.emi_paid)], ["Fuel", inr(life.fuel_cost)], ["Repairs", inr(life.repair_cost)],
          ["Other", inr(life.other_cost)], ["Disposal", inr(life.disposal_costs)], ["Purchase", life.purchase_value ? inr(life.purchase_value) : "—"]].map(([l, v]) => (
          <div key={l}><span className="text-slate-400">{l}:</span> <b>{v}</b></div>
        ))}
      </div>
      <div className="mt-2 border-t border-white/10 pt-2 grid grid-cols-2 gap-1.5 text-[11px]">
        <div><span className="text-slate-400">Total cost:</span> <b>{inr(life.total_lifetime_cost)}</b></div>
        <div><span className="text-slate-400">Sale proceeds:</span> <b className="text-emerald-400">{inr(life.sale_proceeds)}</b></div>
        <div><span className="text-slate-400">Net cost:</span> <b className="text-[#BF9B30]">{inr(life.net_lifetime_cost)}</b></div>
        <div><span className="text-slate-400">Net/km · /TD:</span> <b>{life.net_cost_per_km != null ? `₹${life.net_cost_per_km}` : "—"} · {life.net_cost_per_td != null ? inr(life.net_cost_per_td) : "—"}</b></div>
      </div>
    </div>
  );
}

function SaleForm({ vehicle, onClose, onDone }) {
  const [f, setF] = useState({ sale_date: new Date().toISOString().slice(0, 10), sale_price: "", buyer_type: "used_car_buyer",
    buyer_name: "", disposal_costs: "", disposal_cost_note: "", payment_reference: "", sale_doc_photo: null });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      const r = await api.post(`/accounts/disposal/${vehicle.id}/sale`, { ...f,
        sale_price: parseFloat(f.sale_price), disposal_costs: f.disposal_costs ? parseFloat(f.disposal_costs) : 0 });
      toast.success(`${vehicle.reg_no} sold · net lifetime cost ${inr(r.data.lifetime.net_lifetime_cost)}`);
      onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div data-testid="sale-form-modal" className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold text-[#0E2240]">Record Sale · {vehicle.reg_no}</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div><label className={lbl}>Sale date *</label><input type="date" value={f.sale_date} onChange={set("sale_date")} className={inputCls} /></div>
          <div><label className={lbl}>Sale price (₹) *</label><input data-testid="sale-price-input" type="number" value={f.sale_price} onChange={set("sale_price")} className={inputCls} /></div>
          <div><label className={lbl}>Buyer type</label>
            <select data-testid="sale-buyer-type" value={f.buyer_type} onChange={set("buyer_type")} className={inputCls}>
              {[["used_car_buyer", "Used-car buyer"], ["exchange", "Exchange"], ["staff", "Staff"], ["auction", "Auction"], ["other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><label className={lbl}>Buyer name / ref</label><input value={f.buyer_name} onChange={set("buyer_name")} className={inputCls} /></div>
          <div><label className={lbl}>Disposal costs (₹)</label><input type="number" value={f.disposal_costs} onChange={set("disposal_costs")} placeholder="Refurb, transport…" className={inputCls} /></div>
          <div><label className={lbl}>Cost note</label><input value={f.disposal_cost_note} onChange={set("disposal_cost_note")} className={inputCls} /></div>
          <div className="col-span-2"><label className={lbl}>Payment reference</label><input value={f.payment_reference} onChange={set("payment_reference")} className={inputCls} /></div>
        </div>
        <div className="mt-3"><PhotoInput label="sale document (optional)" value={f.sale_doc_photo} onChange={(p) => setF({ ...f, sale_doc_photo: p })} testId="sale-doc-photo-btn" /></div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
          <button data-testid="sale-submit-btn" onClick={submit} className="rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white">Record Sale</button>
        </div>
      </div>
    </div>
  );
}

function Pending({ canSell }) {
  const [data, setData] = useState(null);
  const [selling, setSelling] = useState(null);
  const load = () => api.get("/accounts/disposal-fleet").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="mt-4 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="mt-4 space-y-3">
      {data.vehicles.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No vehicles pending disposal</div>}
      {data.vehicles.map(({ vehicle: v, handover: h, days_pending, overdue, lifetime }) => (
        <div key={v.id} data-testid={`disposal-card-${v.reg_no}`} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="font-heading font-bold text-[#0E2240]">{v.reg_no} · {v.model} {v.variant}</div>
              <div className="text-[11px] text-slate-500">{v.branch_id} · final odo {(v.current_odometer || 0).toLocaleString("en-IN")} km{h && ` · fuel ${h.fuel_level} · by ${h.requested_by_name}`}</div>
              {h?.condition_notes && <div className="text-[11px] text-slate-400 mt-0.5">“{h.condition_notes}”</div>}
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${overdue ? "bg-red-100 text-red-700" : "bg-fuchsia-100 text-fuchsia-700"}`}>
              {days_pending}d pending{overdue && ` · > ${data.alert_days}d`}
            </span>
          </div>
          {h?.photos?.length > 0 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto">
              {h.photos.map((p, i) => <img key={i} src={p} alt="" className="h-14 w-14 rounded-lg object-cover" />)}
            </div>
          )}
          <PnLCard life={lifetime} />
          {canSell && (
            <button data-testid={`record-sale-btn-${v.reg_no}`} onClick={() => setSelling(v)}
              className="mt-3 w-full rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white">Record Sale</button>
          )}
        </div>
      ))}
      {selling && <SaleForm vehicle={selling} onClose={() => setSelling(null)} onDone={load} />}
    </div>
  );
}

function Archive() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/accounts/disposal-archive", { params: { q: q || undefined } }).then((r) => setRows(r.data)); }, [q]);
  return (
    <div className="mt-4">
      <input data-testid="archive-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reg no…" className={inputCls} />
      <div className="mt-3 space-y-2">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">Archive is empty</div>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`archive-row-${r.reg_no}`} className="rounded-xl border border-slate-200 bg-white p-3.5">
            <button className="w-full text-left" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-heading font-bold text-[#0E2240]">{r.reg_no}</span>
                  <span className="ml-2 text-[11px] text-slate-500">{r.model} · {r.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  {r.kind === "sold" && <span className="text-xs font-bold text-emerald-700">{inr(r.sale_price)}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${r.kind === "sold" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{r.kind}</span>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Net lifetime cost {inr(r.lifetime?.net_lifetime_cost)}{r.lifetime?.net_cost_per_km != null && ` · ₹${r.lifetime.net_cost_per_km}/km`}
                {r.kind === "sold" ? ` · ${(r.buyer_type || "").replace(/_/g, " ")}${r.buyer_name ? ` (${r.buyer_name})` : ""}` : ` · ${(r.reason || "").replace(/_/g, " ")}`}
              </div>
            </button>
            {open === r.id && <PnLCard life={r.lifetime} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/accounts/disposal-reports").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="mt-4 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Monthly disposal report</h3>
        <table className="mt-2 w-full text-left text-xs">
          <thead className="text-[10px] uppercase text-slate-400"><tr>{["Month", "Sold", "Proceeds", "Avg net cost"].map((h) => <th key={h} className="py-1.5">{h}</th>)}</tr></thead>
          <tbody>
            {data.monthly.map((m) => (
              <tr key={m.month} className="border-t border-slate-100">
                <td className="py-2 font-bold text-[#0E2240]">{m.month}</td><td>{m.count}</td>
                <td>{inr(m.proceeds)}</td><td>{inr(m.avg_net_cost)}</td>
              </tr>
            ))}
            {data.monthly.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">No sales yet</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Lifetime-cost ranking (cheapest to run)</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">Direct input to future demo fleet purchase decisions</p>
        <div className="mt-2 space-y-1.5">
          {data.model_ranking.map((m, i) => (
            <div key={m.model} className="flex justify-between rounded-lg bg-slate-50 p-2.5 text-xs">
              <span className="font-semibold text-[#0E2240]">{i + 1}. {m.model} <span className="text-slate-400">({m.count} disposed)</span></span>
              <span className="font-bold text-[#BB162B]">{inr(m.avg_net_lifetime_cost)}{m.avg_net_cost_per_km != null && ` · ₹${m.avg_net_cost_per_km}/km`}</span>
            </div>
          ))}
          {data.model_ranking.length === 0 && <div className="py-4 text-center text-xs text-slate-400">No disposals yet</div>}
        </div>
      </div>
    </div>
  );
}

export default function DisposalTab() {
  const { user } = useAuth();
  const [sub, setSub] = useState("pending");
  const canSell = ["aho", "azh", "admin"].includes(user.role);
  return (
    <div>
      <div className="mt-4 flex rounded-xl bg-slate-100 p-1 max-w-md">
        {[["pending", "Pending Disposal"], ["archive", "Archive"], ["reports", "Reports"]].map(([t, l]) => (
          <button key={t} data-testid={`disposal-sub-${t}`} onClick={() => setSub(t)}
            className={`flex-1 rounded-lg py-2 text-xs font-bold ${sub === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {sub === "pending" && <Pending canSell={canSell} />}
      {sub === "archive" && <Archive />}
      {sub === "reports" && <Reports />}
    </div>
  );
}
